import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { currentStoreIdSelector } from '../../stores/ducks/stores.selector';
import {
  createAutoResponseRuleApi,
  createPostApi,
  createReplyTemplateApi,
  createServiceAreaApi,
  deleteAutoResponseRuleApi,
  deletePhotoApi,
  deletePostApi,
  deleteReplyTemplateApi,
  deleteServiceAreaApi,
  disconnectSocialApi,
  downloadSeoReportApi,
  generateAiPostApi,
  generateAiReplyApi,
  generateSeoReportApi,
  getAiSettingsApi,
  getAnalyticsApi,
  getAutoResponseRulesApi,
  getDatapointApi,
  getDatapointsApi,
  getKeywordRankingsApi,
  getKeywordsApi,
  getListingsApi,
  getLocationProfileApi,
  getPhotosApi,
  getPostDirectoriesApi,
  getPostsApi,
  getReplyTemplatesApi,
  getScoreApi,
  getSocialConnectionsApi,
  getStoreServiceAreasApi,
  getStoreUberallLinkApi,
  markDatapointReadApi,
  patchAiSettingsApi,
  patchLocationApi,
  putKeywordsApi,
  ReplyTemplateBody,
  replyToDatapointApi,
  ServiceAreaBody,
  sendSeoReportApi,
  syncListingsApi,
  updateAutoResponseRuleApi,
  updatePostApi,
  updateReplyTemplateApi,
  updateServiceAreaApi,
  uploadPhotoApi,
  uploadPostVisualApi,
} from '../api';
import { UberallAiPostBody, UberallPhotoSlot, UberallSocialPlatform, UberallSocialPostBody } from '../api/contract';
import { uberallErrorCode, uberallErrorMessage } from '../api/errors';
import { MAX_SERVICE_AREAS, SERVICE_AREAS_WRITABLE, SOCIAL_DISCONNECT_RECHECK_MS } from '../constants';
import { LocationPatch, UberallAiSettingsPayload, UberallAutoResponseRuleBody } from '../type';
import { comparisonWindow, saveFile, sendableLocationPatch, seoReportFileName } from '../utils/visibility.utils';

const tr = (key: string, params?: Record<string, unknown>) =>
  i18n.t(`visibility.${key}`, { ns: 'visibility', ...params });

const NO_STORE = 'NO_STORE';
const NO_LOCATION = 'NO_LOCATION';
const UNKNOWN_FAILURE = 'UNKNOWN';
export const REVIEW_GONE_CODES = ['UBERALL_REVIEW_GONE', 'UBERALL_NOT_FOUND'];

const storeIdOf = (state: RootState) => currentStoreIdSelector(state);

const locationIdOf = (state: RootState): number | null => {
  const locationId = state.visibility.link.data?.locationId;
  return typeof locationId === 'number' ? locationId : null;
};

const failureCode = (error: unknown): string => uberallErrorCode(error) ?? UNKNOWN_FAILURE;

const translate = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: 'visibility', ...options });

const reportFailure = (dispatch: (action: unknown) => void, error: unknown) => {
  dispatch(showMessage({ message: uberallErrorMessage(error, translate), variant: 'error' }));
};

export const fetchVisibilityLinkThunk = createAsyncThunk(
  'visibility/fetchLink',
  async (_, { getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      return await getStoreUberallLinkApi(storeId);
    } catch (error: unknown) {
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchAiSettingsThunk = createAsyncThunk(
  'visibility/fetchAiSettings',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      return await getAiSettingsApi(storeId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveAiSettingsThunk = createAsyncThunk(
  'visibility/saveAiSettings',
  async (patch: Partial<UberallAiSettingsPayload>, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      const saved = await patchAiSettingsApi(storeId, patch);
      dispatch(showMessage({ message: tr('toasts.aiSettingsSaved'), variant: 'success' }));
      return saved;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchReplyTemplatesThunk = createAsyncThunk(
  'visibility/fetchReplyTemplates',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      return await getReplyTemplatesApi(storeId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveReplyTemplateThunk = createAsyncThunk(
  'visibility/saveReplyTemplate',
  async ({ id, body }: { id: string | null; body: ReplyTemplateBody }, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      if (id) await updateReplyTemplateApi(storeId, id, body);
      else await createReplyTemplateApi(storeId, body);
      dispatch(showMessage({ message: tr('toasts.templateSaved'), variant: 'success' }));
      return await getReplyTemplatesApi(storeId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const deleteReplyTemplateThunk = createAsyncThunk(
  'visibility/deleteReplyTemplate',
  async (templateId: string, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      await deleteReplyTemplateApi(storeId, templateId);
      return await getReplyTemplatesApi(storeId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

/**
 * Pushes the ready (placeId-present) subset of the local service-area list to Uberall via
 * the same location PATCH the rest of the profile already uses — no new backend call. A
 * sync failure is reported softly: the local save that triggered it already succeeded, so
 * this must never read as if the merchant's edit itself was lost.
 */
export const syncServiceAreasToUberallThunk = createAsyncThunk(
  'visibility/syncServiceAreasToUberall',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = locationIdOf(state);
    if (!locationId) return rejectWithValue(NO_LOCATION);

    const ready = state.visibility.serviceAreas.data
      .filter(area => Boolean(area.placeId))
      .map(area => ({ name: area.name, placeId: area.placeId as string }));

    if (ready.length > MAX_SERVICE_AREAS) {
      dispatch(
        showMessage({
          message: tr('toasts.serviceAreaTooManyReady', { max: MAX_SERVICE_AREAS }),
          variant: 'warning',
        }),
      );
      return rejectWithValue('TOO_MANY_READY');
    }

    try {
      return await patchLocationApi(locationId, { serviceAreas: ready });
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('toasts.serviceAreaSyncFailed'), variant: 'warning' }));
      return rejectWithValue(failureCode(error));
    }
  },
);

/**
 * On first load only: if the local list is empty but the listing already has real Uberall
 * service areas (added before this feature existed), import them as pre-synced local
 * entries so nothing looks lost.
 */
export const fetchServiceAreasThunk = createAsyncThunk(
  'visibility/fetchServiceAreas',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      let areas = await getStoreServiceAreasApi(storeId);
      const locationId = locationIdOf(getState() as RootState);
      if (areas.length === 0 && locationId) {
        const profile = await getLocationProfileApi(locationId);
        const existing = profile.location.serviceAreas ?? [];
        for (const area of existing) {
          await createServiceAreaApi(storeId, { name: area.name, placeId: area.placeId });
        }
        if (existing.length > 0) areas = await getStoreServiceAreasApi(storeId);
      }
      return areas;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveServiceAreaThunk = createAsyncThunk(
  'visibility/saveServiceArea',
  async ({ id, body }: { id: string | null; body: ServiceAreaBody }, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      if (id) await updateServiceAreaApi(storeId, id, body);
      else await createServiceAreaApi(storeId, body);
      dispatch(showMessage({ message: tr('toasts.serviceAreaSaved'), variant: 'success' }));
      const areas = await getStoreServiceAreasApi(storeId);
      dispatch(syncServiceAreasToUberallThunk());
      return areas;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const deleteServiceAreaThunk = createAsyncThunk(
  'visibility/deleteServiceArea',
  async (areaId: string, { dispatch, getState, rejectWithValue }) => {
    const storeId = storeIdOf(getState() as RootState);
    if (!storeId) return rejectWithValue(NO_STORE);
    try {
      await deleteServiceAreaApi(storeId, areaId);
      const areas = await getStoreServiceAreasApi(storeId);
      dispatch(syncServiceAreasToUberallThunk());
      return areas;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchAutoResponseRulesThunk = createAsyncThunk(
  'visibility/fetchAutoResponseRules',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getAutoResponseRulesApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveAutoResponseRuleThunk = createAsyncThunk(
  'visibility/saveAutoResponseRule',
  async (
    { id, body }: { id: string | null; body: UberallAutoResponseRuleBody },
    { dispatch, getState, rejectWithValue },
  ) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const saved = id
        ? await updateAutoResponseRuleApi(locationId, id, body)
        : await createAutoResponseRuleApi(locationId, body);
      dispatch(showMessage({ message: tr('toasts.autoResponseSaved'), variant: 'success' }));
      return saved;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const deleteAutoResponseRuleThunk = createAsyncThunk(
  'visibility/deleteAutoResponseRule',
  async (ruleId: string, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const remaining = await deleteAutoResponseRuleApi(locationId, ruleId);
      dispatch(showMessage({ message: tr('toasts.autoResponseDeleted'), variant: 'success' }));
      return remaining;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchScoreThunk = createAsyncThunk(
  'visibility/fetchScore',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getScoreApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchProfileThunk = createAsyncThunk(
  'visibility/fetchProfile',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getLocationProfileApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveProfileThunk = createAsyncThunk(
  'visibility/saveProfile',
  async (patch: LocationPatch, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const sendable = sendableLocationPatch(patch, SERVICE_AREAS_WRITABLE) as Record<string, unknown>;
      const saved = await patchLocationApi(locationId, sendable);
      dispatch(showMessage({ message: tr('toasts.profileSaved'), variant: 'success' }));
      return saved;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const generateSeoReportThunk = createAsyncThunk(
  'visibility/generateSeoReport',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const pdf = await generateSeoReportApi(locationId);
      const generatedAt = new Date().toISOString();
      saveFile(pdf, seoReportFileName(generatedAt));
      dispatch(showMessage({ message: tr('toasts.reportGenerated'), variant: 'success' }));
      return { generatedAt };
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const downloadSeoReportThunk = createAsyncThunk(
  'visibility/downloadSeoReport',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = locationIdOf(state);
    const generatedAt = state.visibility.link.data?.lastReportGeneratedAt;
    if (!locationId) return rejectWithValue(NO_LOCATION);
    if (!generatedAt) return rejectWithValue('SEO_REPORT_NOT_GENERATED');
    try {
      saveFile(await downloadSeoReportApi(locationId), seoReportFileName(generatedAt));
      return undefined;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const sendSeoReportThunk = createAsyncThunk(
  'visibility/sendSeoReport',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const sent = await sendSeoReportApi(locationId);
      dispatch(showMessage({ message: tr('toasts.reportSent', { email: sent.sentTo }), variant: 'success' }));
      return sent;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchListingsThunk = createAsyncThunk(
  'visibility/fetchListings',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getListingsApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const syncListingsThunk = createAsyncThunk(
  'visibility/syncListings',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const synced = await syncListingsApi(locationId);
      dispatch(showMessage({ message: tr('toasts.syncStarted'), variant: 'success' }));
      return synced;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchPhotosThunk = createAsyncThunk(
  'visibility/fetchPhotos',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getPhotosApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const uploadPhotoThunk = createAsyncThunk(
  'visibility/uploadPhoto',
  async ({ slot, file }: { slot: UberallPhotoSlot; file: File }, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const photos = await uploadPhotoApi(locationId, slot, file);
      dispatch(showMessage({ message: tr('toasts.photoUploaded'), variant: 'success' }));
      return photos;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const deletePhotoThunk = createAsyncThunk(
  'visibility/deletePhoto',
  async (photoId: number, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await deletePhotoApi(locationId, photoId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchKeywordsThunk = createAsyncThunk(
  'visibility/fetchKeywords',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getKeywordsApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const saveKeywordsThunk = createAsyncThunk(
  'visibility/saveKeywords',
  async (keywords: string[], { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await putKeywordsApi(locationId, keywords);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchDashboardThunk = createAsyncThunk(
  'visibility/fetchDashboard',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = locationIdOf(state);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    const { range, comparison } = state.visibility.dashboard;
    try {
      return await getAnalyticsApi(locationId, range.from, range.to, comparisonWindow(range, comparison));
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchReviewsThunk = createAsyncThunk(
  'visibility/fetchReviews',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = locationIdOf(state);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    const { page, size, filter } = state.visibility.reviews;
    try {
      return await getDatapointsApi(locationId, {
        page,
        limit: size,
        answered: filter.answered,
        startDate: filter.from ?? undefined,
        endDate: filter.to ?? undefined,
      });
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchUnansweredCountThunk = createAsyncThunk(
  'visibility/fetchUnansweredCount',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = locationIdOf(state);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const { unansweredCount } = await getDatapointsApi(locationId, { page: 1, limit: 1 });
      return unansweredCount;
    } catch (error: unknown) {
      // Deliberately still latches hasCountLoaded (see the slice's .rejected case) so this
      // doesn't retry on every render — but a failure must not be silent either.
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchReviewReplyThunk = createAsyncThunk(
  'visibility/fetchReviewReply',
  async (datapointId: number, { rejectWithValue }) => {
    try {
      return await getDatapointApi(datapointId);
    } catch (error: unknown) {
      return rejectWithValue(failureCode(error));
    }
  },
);

export const markReviewReadThunk = createAsyncThunk(
  'visibility/markReviewRead',
  async (datapointId: number, { getState, rejectWithValue }) => {
    const review = (getState() as RootState).visibility.reviews.data.find(entry => entry.id === datapointId);
    if (!review || review.read === true) return rejectWithValue('ALREADY_READ');
    try {
      await markDatapointReadApi(datapointId);
      return datapointId;
    } catch (error: unknown) {
      return rejectWithValue(failureCode(error));
    }
  },
);

export const publishReplyThunk = createAsyncThunk(
  'visibility/publishReply',
  async ({ datapointId, text }: { datapointId: number; text: string }, { dispatch, rejectWithValue }) => {
    try {
      await replyToDatapointApi(datapointId, text);
      dispatch(showMessage({ message: tr('toasts.replyPublished'), variant: 'success' }));
      return datapointId;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      // No reload when the review is gone: Uberall keeps listing it for a while, so a reload
      // would put it straight back. The slice drops it instead.
      return rejectWithValue({ datapointId, code: failureCode(error) });
    }
  },
);

export const generateAiReplyThunk = createAsyncThunk(
  'visibility/generateAiReply',
  async (
    { datapointId, instruction }: { datapointId: number; instruction?: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await generateAiReplyApi(datapointId, instruction);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue({ datapointId, code: failureCode(error) });
    }
  },
);

export const generateAiPostThunk = createAsyncThunk(
  'visibility/generateAiPost',
  async (body: UberallAiPostBody, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await generateAiPostApi(locationId, body);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchKeywordRankingsThunk = createAsyncThunk(
  'visibility/fetchKeywordRankings',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getKeywordRankingsApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
  // The dashboard and Analytics both ask for it, and one read can take Uberall 20 seconds.
  { condition: (_, { getState }) => !(getState() as RootState).visibility.keywordRankings.isLoading },
);

export const fetchPostDirectoriesThunk = createAsyncThunk(
  'visibility/fetchPostDirectories',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return (await getPostDirectoriesApi(locationId)).directories;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
  { condition: (_, { getState }) => !(getState() as RootState).visibility.postDirectories.isLoading },
);

/** `pinnedLocationId` reads one location whatever the current store is: the connect popup polls the one it opened for. */
export const fetchSocialConnectionsThunk = createAsyncThunk(
  'visibility/fetchSocialConnections',
  async (pinnedLocationId: number | undefined, { getState, rejectWithValue }) => {
    const locationId = pinnedLocationId ?? locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getSocialConnectionsApi(locationId);
    } catch (error: unknown) {
      // Silent: the composer keeps Google usable without it and the connect popup polls this every few seconds.
      return rejectWithValue(failureCode(error));
    }
  },
);

export const disconnectSocialThunk = createAsyncThunk(
  'visibility/disconnectSocial',
  async (platform: UberallSocialPlatform, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const connections = await disconnectSocialApi(locationId, platform);
      dispatch(showMessage({ message: tr('toasts.socialDisconnected'), variant: 'success' }));
      // Uberall can still report the account connected right after the delete; one later read settles it.
      setTimeout(() => dispatch(fetchSocialConnectionsThunk()), SOCIAL_DISCONNECT_RECHECK_MS);
      return connections;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const fetchPostsThunk = createAsyncThunk(
  'visibility/fetchPosts',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await getPostsApi(locationId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

/**
 * `visual` is a file to upload before the write; `body.imageUrl` is the URL semantics themselves.
 * The body is passed through untouched when there is no file, which is what keeps the three states
 * apart: an absent key keeps the current visual, an explicit null clears it.
 */
export const savePostThunk = createAsyncThunk(
  'visibility/savePost',
  async (
    { id, body, visual }: { id: number | null; body: UberallSocialPostBody; visual?: File | null },
    { dispatch, getState, rejectWithValue },
  ) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      const written = visual ? { ...body, imageUrl: await uploadPostVisualApi(locationId, visual) } : body;
      const list = id ? await updatePostApi(locationId, id, written) : await createPostApi(locationId, written);
      const isImmediate = new Date(body.publicationDate).getTime() <= Date.now();
      dispatch(
        showMessage({
          message: tr(isImmediate ? 'toasts.postPublished' : 'toasts.postScheduled'),
          variant: 'success',
        }),
      );
      return list;
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);

export const deletePostThunk = createAsyncThunk(
  'visibility/deletePost',
  async (postId: number, { dispatch, getState, rejectWithValue }) => {
    const locationId = locationIdOf(getState() as RootState);
    if (!locationId) return rejectWithValue(NO_LOCATION);
    try {
      return await deletePostApi(locationId, postId);
    } catch (error: unknown) {
      reportFailure(dispatch, error);
      return rejectWithValue(failureCode(error));
    }
  },
);
